import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { submitOnboardingQuiz } from '../lib/api';
import { toast } from 'sonner';

const GOALS = [
  { id: 'fitness', label: 'Get fit and build strength' },
  { id: 'bjj', label: 'Learn Brazilian Jiu-Jitsu' },
  { id: 'self_defense', label: 'Learn self-defense' },
  { id: 'competition', label: 'Train for competition' },
  { id: 'other', label: 'Other' },
];

const DISCIPLINES = [
  { id: 'jiujitsu', label: 'Brazilian Jiu-Jitsu' },
  { id: 'self_defense', label: 'Self-Defense' },
  { id: 'personal_training', label: 'Personal Training' },
  { id: 'not_sure', label: 'Not sure yet' },
];

const EXPERIENCE = [
  { id: 'beginner', label: 'Complete beginner' },
  { id: 'some', label: 'Some experience' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

const Choice = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left px-4 py-3 rounded-md border text-sm transition-colors ${
      active
        ? 'border-[#C0A062] bg-[#C0A062]/10 text-white'
        : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'
    }`}
  >
    {children}
  </button>
);

const ClientOnboardingQuiz = () => {
  const navigate = useNavigate();
  const { user, refreshUser, isClient, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goal, setGoal] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [experience, setExperience] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !isClient) return;
    const cp = user?.client_profile || {};
    // Skip for admin accounts and users that already completed onboarding
    const isAdmin = (user?.email || '').toLowerCase() === 'admin@doorguard.app';
    if (!isAdmin && !cp.onboarding_completed_at) {
      setOpen(true);
    }
  }, [user, isAuthenticated, isClient]);

  const finishAndGo = async (skipping = false) => {
    setSubmitting(true);
    try {
      await submitOnboardingQuiz({
        onboarding_goal: skipping ? null : (goal || null),
        onboarding_discipline: skipping ? null : (discipline || null),
        onboarding_experience: skipping ? null : (experience || null),
      });
      await refreshUser();
      setOpen(false);
      navigate('/trainers');
      if (!skipping) toast.success('Got it, finding coaches that match.');
    } catch (e) {
      toast.error('Could not save your answers');
    } finally {
      setSubmitting(false);
    }
  };

  const allAnswered = goal && discipline && experience;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="bg-[#18181B] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="client-onboarding-quiz"
      >
        <DialogHeader>
          <DialogTitle className="text-white text-2xl">
            Let&apos;s find the right coach for you.
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div>
            <p className="text-sm text-zinc-400 mb-3">What are your goals?</p>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="quiz-goals">
              {GOALS.map((o) => (
                <Choice key={o.id} active={goal === o.id} onClick={() => setGoal(o.id)} testid={`quiz-goal-${o.id}`}>
                  {o.label}
                </Choice>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm text-zinc-400 mb-3">Which discipline interests you most?</p>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="quiz-disciplines">
              {DISCIPLINES.map((o) => (
                <Choice key={o.id} active={discipline === o.id} onClick={() => setDiscipline(o.id)} testid={`quiz-discipline-${o.id}`}>
                  {o.label}
                </Choice>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm text-zinc-400 mb-3">What&apos;s your experience level?</p>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="quiz-experience">
              {EXPERIENCE.map((o) => (
                <Choice key={o.id} active={experience === o.id} onClick={() => setExperience(o.id)} testid={`quiz-experience-${o.id}`}>
                  {o.label}
                </Choice>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 items-center sm:justify-between w-full">
          <button
            type="button"
            onClick={() => finishAndGo(true)}
            disabled={submitting}
            className="text-sm text-zinc-500 hover:text-zinc-300 underline"
            data-testid="quiz-skip"
          >
            Skip for now
          </button>
          <Button
            type="button"
            onClick={() => finishAndGo(false)}
            disabled={submitting || !allAnswered}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
            data-testid="quiz-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Find my coach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClientOnboardingQuiz;
